import { LEGACY_EVENTS } from 'gcs-common/config/googleAnalyticsLegacyConfig';
import { getErrorMsg } from 'gcs-common/helper/errorMessages';
import { getMediaFileSizeLimit } from 'gcs-common/helper/fileHelper';
import { MAX_MESSAGE_LENGTH } from 'gcs-common/helper/messagesHelpers';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { trackGALegacyEvent } from 'gcs-common/slices/googleAnalytics/googleAnalyticsThunks';
import { channelInputFileStaged } from 'gcs-common/slices/messageInput/messageInputSlice';
import { useCallback, useEffect, useRef } from 'react';
import TextareaAutosize from 'react-autosize-textarea';
import { useDispatch, useSelector } from 'react-redux';
import usePasteMedia from '../../../customHooks/usePasteMedia';
import { showFailureNotification } from '../../../slices/notifications/ShowNotificationThunk';
import styles from './styles.module.scss';

interface MessageInputFieldProps {
  inputText: string;
  placeholder: string;
  disabled: boolean;
  sendingDisabled: boolean;
  onSendMessage: () => void;
  onTextChange: (text: string) => void;
}
const MessageInputField = (props: MessageInputFieldProps) => {
  const { inputText, placeholder, disabled, sendingDisabled, onSendMessage, onTextChange } = props;
  const selectedChannelId = useSelector(getSelectedChannelId);
  const dispatch = useDispatch();

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  interface FileWithSize {
    size: number;
    name: string;
    type?: string;
  }

  const onPasteMedia = useCallback(
    (files: FileWithSize[]) => {
      if (files.length) {
        for (const file of files) {
          const fileSizeLimit = getMediaFileSizeLimit(file as File);
          if (file.size > fileSizeLimit) {
            const errorMessage = getErrorMsg('files.wrongFileSize', file.name, fileSizeLimit / 1000000);
            dispatch(showFailureNotification(errorMessage));
          } else {
            dispatch(channelInputFileStaged({ channelId: selectedChannelId, file }));
          }
        }
        textAreaRef.current?.focus();
      }
    },
    [dispatch, selectedChannelId],
  );

  usePasteMedia(onPasteMedia);

  const onKeyDown = (event: { keyCode: number; shiftKey: boolean; preventDefault: () => void }) => {
    if (event.keyCode === 13 && !event.shiftKey && !sendingDisabled) {
      event.preventDefault();
      onSendMessage();
    }
  };

  const onMessageChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(event.target.value);
  };

  const onFocus = useCallback(() => {
    dispatch(trackGALegacyEvent(LEGACY_EVENTS.BEGIN_FOCUS()));
  }, [dispatch]);

  useEffect(() => {
    if (selectedChannelId) {
      textAreaRef.current?.focus();
    }
  }, [selectedChannelId]);

  return (
    <TextareaAutosize
      ref={textAreaRef}
      id="msgInput"
      maxRows={12}
      disabled={disabled}
      onChange={onMessageChanged}
      value={inputText}
      className={styles.writeInput}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
      maxLength={MAX_MESSAGE_LENGTH}
    />
  );
};

export default MessageInputField;
